// Claiming a device — adding one that already exists on the server to THIS user,
// by typing its Unique Number.
//
// ⚠️ A UNIQUE NUMBER IS NOT A PERMISSION. It is printed on the device, so until
// 2026-10-11 anyone who read the label could add someone else's device — a door
// lock included — together with access to its room and location. A typed number
// now works only for a device that is YOURS to add: you own it, you own its
// location, you are a Super Admin, or nobody owns it yet (a fresh piece). Anyone
// else is sent to the share QR (core/share/shareAccess), which the server signs
// and expires. The old Flutter share code (`u%#<uid>n%#<uno>`) is refused for
// the same reason: it never expired.
//
// ⚠️ Client-side only — the server's AddUserDeviceMapping is still open (see
// docs/21-share-access.md). This closes the app path, not the API.
//
// Port of shareDeviceFunctions.dart. The work is not just "insert a mapping":
// the device sits in a location and a room that the new user may not be mapped
// to, and without those the device would be claimed but invisible — it would
// belong to you and appear in no room. So the location and room mappings are
// backfilled first, then the device mapping.

import {
  getDeviceDetail, getDevicesByUser, getLocations, getRooms,
  addUserLocationMapping, addLocationRoomsMapping, addUserDeviceMapping,
} from '../api/api';
import { resyncSelf } from '../db/markSinkData';

const asInt = (v) => {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isNaN(n) ? 0 : n;
};

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);

// "u%#12n%#5585891" -> "5585891". A bare unique number is accepted too, so a
// plain-text code or a hand-typed number both work.
export function parseSharedUniqueNumber(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const idx = s.indexOf('n%#');
  if (idx !== -1) {
    const uno = s.slice(idx + 3).trim();
    return uno || null;
  }
  return /^[A-Za-z0-9_-]+$/.test(s) ? s : null;
}

/**
 * May this user add the device by its number alone? Owner of the device or of
 * its location, a Super Admin, or a device with no owner yet.
 */
export function mayClaimByNumber(detail, myUserId, { isSuperAdmin = false } = {}) {
  if (isSuperAdmin) return true;
  const me = asInt(myUserId);
  const owner = asInt(detail?.DeviceMasterUserId ?? detail?.MasterUserId);
  const locationOwner = asInt(detail?.LocationMasterUserId);
  return owner === 0 || owner === me || (locationOwner > 0 && locationOwner === me);
}

const ASK_FOR_QR = {
  ok: false,
  title: 'Ask the Owner to Share It',
  message: 'This device belongs to someone else. Ask them to open it in their app, '
    + 'choose "Share (QR)" and let you scan the code — it works for 5 minutes.',
};

// -> { ok, title, message, device? }
export async function claimDevice(myUserId, rawCode, { isSuperAdmin = false } = {}) {
  // The old share QR carries no expiry, so a photo of it worked forever.
  if (/u%#\d+n%#/.test(String(rawCode || ''))) {
    return {
      ok: false,
      title: 'Old Share Code',
      message: 'This share code is from an older app and is no longer accepted. '
        + 'Ask the owner for a new QR — it works for 5 minutes.',
    };
  }
  const uno = parseSharedUniqueNumber(rawCode);
  if (!uno) {
    return { ok: false, title: 'Invalid Code', message: "That doesn't look like a device code." };
  }

  // 1. Does the device exist?
  const detail = rows(await getDeviceDetail(uno).catch(() => null))[0];
  if (!detail) {
    return {
      ok: false,
      title: 'Device Not Found',
      message: `No device found with unique number ${uno}.`,
    };
  }

  const installedId = detail.ID ?? detail.Id;
  if (!installedId) {
    return {
      ok: false,
      title: 'Device Not Found',
      message: 'The device record is incomplete, so it cannot be added.',
    };
  }

  if (!mayClaimByNumber(detail, myUserId, { isSuperAdmin })) return ASK_FOR_QR;

  // 2. Already mine? Claiming twice would create a duplicate mapping row.
  const mine = rows(await getDevicesByUser(myUserId).catch(() => null));
  if (mine.some((d) => String(d.UniqueNumber) === String(uno))) {
    return {
      ok: false,
      title: 'Already Added',
      message: 'This device is already in your account.',
    };
  }

  // 3. Backfill the location mapping, or the device lands somewhere invisible.
  const locationId = detail.LocationID ?? detail.LocationId;
  if (asInt(locationId) > 0) {
    const myLocations = rows(await getLocations(myUserId).catch(() => null));
    const hasLocation = myLocations
      .some((l) => String(l.LocationID) === String(locationId));
    if (!hasLocation) {
      await addUserLocationMapping(myUserId, locationId).catch(() => {});
    }

    // 4. Same for the room (a common device has none — RoomID 0).
    const roomId = detail.RoomID ?? detail.RoomId;
    if (asInt(roomId) > 0) {
      const myRooms = rows(await getRooms(myUserId, locationId).catch(() => null));
      const hasRoom = myRooms.some((r) => String(r.RoomID) === String(roomId));
      if (!hasRoom) {
        await addLocationRoomsMapping(myUserId, locationId, roomId).catch(() => {});
      }
    }
  }

  // 5. Finally map the device itself.
  try {
    await addUserDeviceMapping(myUserId, installedId);
  } catch (e) {
    return {
      ok: false,
      title: "Couldn't Add Device",
      message: 'Something went wrong while adding the device. Please try again.',
    };
  }

  // The next screen reads the room local-first, so the new rows must be in
  // the cache BEFORE this returns (same lesson as bleNodes.addBleNode).
  await resyncSelf(myUserId);

  return {
    ok: true,
    title: 'Device Added',
    message: `${detail.DeviceName || uno} has been added to your account.`,
    device: detail,
  };
}
